/**
 * Authentication Store (Zustand)
 *
 * Global state management for authentication
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  getAccessToken,
  setAccessToken,
  removeAccessToken,
  getUserFromToken,
  setRefreshToken,
  getRefreshToken,
  isTokenExpired,
  tokenGrantsApp,
} from '../utils/authUtils';

interface User {
  id: string;
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  [key: string]: any;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  refreshToken: string | null;

  // Actions
  setUser: (user: User | null) => void;
  login: (tokens: { accessToken: string; refreshToken?: string }) => Promise<void>;
  logout: () => void;
  checkAuth: () => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      refreshToken: getRefreshToken(),

      setUser: (user) => {
        set({ user, isAuthenticated: !!user });
      },

      login: async (tokens) => {
        // Store access token
        setAccessToken(tokens.accessToken);

        // Store refresh token if provided
        if (tokens.refreshToken) {
          setRefreshToken(tokens.refreshToken);
          set({ refreshToken: tokens.refreshToken });
        }

        // Extract basic user info from token
        const tokenUser = getUserFromToken();

        // SMA-8: enforce app access. A central token must grant `creative-toolbox`
        // (dev-bypass / claimless tokens pass). Reject otherwise so a user with no
        // access to this app can't hold a session here.
        if (!tokenGrantsApp(tokenUser)) {
          console.warn('Token does not grant access to creative-toolbox; rejecting login.');
          removeAccessToken();
          set({ user: null, isAuthenticated: false });
          return;
        }

        // The token carries the profile. There is no v3 profile endpoint — the
        // old /api/v1/user/me round-trip 404'd on every sign-in.
        set({ user: tokenUser, isAuthenticated: true });
      },

      logout: () => {
        // Clear all auth data
        removeAccessToken();

        // Reset state
        set({ user: null, isAuthenticated: false, refreshToken: null });

        // Clear persisted store
        localStorage.removeItem('auth-storage');
      },

      checkAuth: async () => {
        const token = getAccessToken();
        const refreshToken = getRefreshToken();

        if (token) {
          // Check if token is expired
          if (isTokenExpired(token)) {
            console.warn('Access token is expired');
            // TODO: Implement token refresh logic here if you have a refresh endpoint
            get().logout();
            set({ isLoading: false });
            return;
          }

          const tokenUser = getUserFromToken();
          if (tokenUser && !tokenGrantsApp(tokenUser)) {
            // SMA-8: valid token but no access to this app — drop the session.
            console.warn('Token does not grant access to creative-toolbox; signing out.');
            get().logout();
            set({ isLoading: false });
            return;
          }
          if (tokenUser) {
            set({ user: tokenUser, isAuthenticated: true, refreshToken, isLoading: false });
          } else {
            // Invalid token
            get().logout();
            set({ isLoading: false });
          }
        } else {
          set({ user: null, isAuthenticated: false, refreshToken: null, isLoading: false });
        }
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        refreshToken: state.refreshToken,
      }),
    }
  )
);
